import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FileText, Shield, Scale, ListChecks, HelpCircle, CheckCircle2, ChevronDown, ArrowRight, Phone } from "lucide-react";
import { isRTL } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumb from "@/components/Breadcrumb";

const sectionIcons = [FileText, Shield, Scale, ListChecks, HelpCircle];
const sectionKeys = ["residency", "tax", "legal", "buying", "faq"];

export default function InvestmentGuidePage() {
  const { t, i18n } = useTranslation();
  const rtl = isRTL(i18n.language);
  const [activeSection, setActiveSection] = useState("residency");
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (key) => {
    setActiveSection(key);
    document.getElementById(`section-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id.replace("section-", ""));
      });
    }, { threshold: 0.3, rootMargin: "-100px 0px" });
    sectionKeys.forEach((key) => {
      const el = document.getElementById(`section-${key}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const faqs = t("invest_guide.faqs", { returnObjects: true }) || [];
  const buyingSteps = t("invest_guide.buying_steps", { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet><title>{t("invest_guide.seo_title")}</title><meta name="description" content={t("invest_guide.seo_desc")} /></Helmet>
      <Header />
      <PageHeader title={t("invest_guide.hero_title")} highlight={t("invest_guide.hero_highlight")} subtitle={t("invest_guide.hero_subtitle")}>
        <Breadcrumb items={[{ label: t("breadcrumb.invest") }]} />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sticky Side Nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <h3 className="text-sm font-semibold text-[#0A192F] uppercase tracking-wider mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("invest_guide.toc")}</h3>
              <nav className="space-y-1">
                {sectionKeys.map((key, i) => {
                  const Icon = sectionIcons[i];
                  return (
                    <button key={key} data-testid={`toc-${key}`} onClick={() => scrollToSection(key)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all ${activeSection === key ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-3 border-[#D4AF37]" : "text-[#4A5568] hover:bg-[#F8F9FA] hover:text-[#0A192F]"}`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {t(`invest_guide.section_${key}`)}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-16">
            {/* Residency */}
            <section id="section-residency" data-testid="section-residency">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.residency_title")}</h2>
              <p className="text-[#4A5568] leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("invest_guide.residency_content")}</p>
              <ul className="space-y-3">
                {(t("invest_guide.residency_items", { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" /><span className="text-[#4A5568] text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{item}</span></li>
                ))}
              </ul>
            </section>

            {/* Tax */}
            <section id="section-tax" data-testid="section-tax" className="border-t border-[#E2E8F0] pt-16">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.tax_title")}</h2>
              <p className="text-[#4A5568] leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("invest_guide.tax_content")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(t("invest_guide.tax_items", { returnObjects: true }) || []).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                    <Shield className="w-5 h-5 text-[#D4AF37] shrink-0" />
                    <span className="text-sm text-[#0A192F] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Legal */}
            <section id="section-legal" data-testid="section-legal" className="border-t border-[#E2E8F0] pt-16">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.legal_title")}</h2>
              <p className="text-[#4A5568] leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("invest_guide.legal_content")}</p>
              <ul className="space-y-3">
                {(t("invest_guide.legal_items", { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" /><span className="text-[#4A5568] text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{item}</span></li>
                ))}
              </ul>
            </section>

            {/* Buying Steps */}
            <section id="section-buying" data-testid="section-buying" className="border-t border-[#E2E8F0] pt-16">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.buying_title")}</h2>
              <div className="space-y-6">
                {buyingSteps.map((step, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#D4AF37] text-white flex items-center justify-center font-bold text-sm">{i + 1}</div>
                    <div className="pt-1">
                      <h3 className="text-base font-semibold text-[#0A192F] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{step.title}</h3>
                      <p className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section id="section-faq" data-testid="section-faq" className="border-t border-[#E2E8F0] pt-16">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.faq_title")}</h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} data-testid={`faq-${i}`} className="border border-[#E2E8F0] rounded-sm overflow-hidden">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#F8F9FA] transition-colors">
                      <span className="text-sm font-medium text-[#0A192F] pr-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-[#4A5568] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-6 pb-4 text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div data-testid="invest-cta" className="bg-[#0A192F] rounded-sm p-8 md:p-12 text-center">
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{t("invest_guide.cta_title")}</h3>
              <p className="text-white/60 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("invest_guide.cta_desc")}</p>
              <Link to={`/${i18n.language}/contact`} className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] text-white font-medium rounded-sm hover:bg-[#B5952F] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("invest_guide.cta_btn")} <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
